import React, { useState } from 'react';
import styled from 'styled-components';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Layout from '../../components/Layout';
import Card from '../../components/Card';

const PageTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: #1A2440;
  margin-bottom: 20px;
`;

const RiskSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RiskSummaryCard = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  text-align: center;
`;

const RiskSummaryTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 10px;
`;

const RiskSummaryValue = styled.div`
  font-size: 32px;
  font-weight: 600;
  color: #1A2440;
`;

const RiskList = styled.div`
  margin-bottom: 30px;
`;

const RiskItem = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 15px;
`;

const RiskHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const RiskIcon = styled.div`
  width: 24px;
  height: 24px;
  background-color: #FFB400;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  margin-right: 10px;
`;

const RiskTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1A2440;
  margin: 0;
`;

const RiskDescription = styled.p`
  font-size: 14px;
  color: #6c757d;
  line-height: 1.5;
  margin-bottom: 0;
`;

const RiskTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #e9ecef;
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #1A2440;
  }
  
  tr:hover {
    background-color: #f8f9fa;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const FilterButton = styled.button<{ active: boolean }>`
  background-color: ${props => props.active ? '#1A2440' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#6c757d'};
  border: 1px solid ${props => props.active ? '#1A2440' : '#e9ecef'};
  border-radius: 4px;
  padding: 8px 12px;
  margin-left: 10px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background-color: ${props => props.active ? '#2c3a5a' : '#e9ecef'};
  }
`;

const SeverityBadge = styled.span<{ severity: 'High' | 'Medium' | 'Low' }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => 
    props.severity === 'High' 
      ? '#ffebee' 
      : props.severity === 'Medium' 
        ? '#fff8e1' 
        : '#e8f5e9'
  };
  color: ${props => 
    props.severity === 'High' 
      ? '#E45858' 
      : props.severity === 'Medium' 
        ? '#FFB400' 
        : '#4CAF50'
  };
`;

const StatusBadge = styled.span<{ status: 'Open' | 'In Progress' | 'Closed' }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => 
    props.status === 'Open' 
      ? '#e3f2fd' 
      : props.status === 'In Progress' 
        ? '#e8f5e9' 
        : '#f5f5f5'
  };
  color: ${props => 
    props.status === 'Open' 
      ? '#2196F3' 
      : props.status === 'In Progress' 
        ? '#4CAF50' 
        : '#9e9e9e'
  };
`;

// Sample risk distribution data
const riskDistributionData = [
  { name: 'Low', value: 4, color: '#4CAF50' },
  { name: 'Medium', value: 3, color: '#8BC34A' },
  { name: 'High', value: 3, color: '#CDDC39' },
];

// Sample flagged risks data
const flaggedRisksData = [
  {
    id: 1,
    title: 'Regulatory Compliance',
    description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor inciro-didum ulabore',
    severity: 'High',
    category: 'Legal',
  },
  {
    id: 2,
    title: 'Competitive Landscape',
    description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor inciro-didum ulabore',
    severity: 'Medium',
    category: 'Market',
  },
];

// Sample all risks data
const allRisksData = [
  { id: 1, title: 'Lorem ipsum dolor sit amet', category: 'Operational', severity: 'High', status: 'Open' },
  { id: 2, title: 'Lorem ipsum dolor sit amet', category: 'Market', severity: 'Medium', status: 'Open' },
  { id: 3, title: 'Lorem ipsum dolor sit amet', category: 'Technology', severity: 'Low', status: 'In Progress' },
  { id: 4, title: 'Lorem ipsum dolor sit amet', category: 'Legal', severity: 'Low', status: 'Open' },
];

const RisksPage: React.FC = () => {
  const [severityFilter, setSeverityFilter] = useState<'All' | 'High' | 'Medium' | 'Low'>('All');

  const filteredRisks = severityFilter === 'All' 
    ? allRisksData 
    : allRisksData.filter(risk => risk.severity === severityFilter);

  return (
    <Layout 
      companyName="Sample Company"
      sector="Software"
      location="United States"
    >
      <PageTitle>Risk Analyzer</PageTitle>
      
      <h3>Risk Summary</h3>
      <RiskSummaryGrid>
        <RiskSummaryCard>
          <RiskSummaryTitle>Total Risks</RiskSummaryTitle>
          <RiskSummaryValue>10</RiskSummaryValue>
        </RiskSummaryCard>
        
        <RiskSummaryCard>
          <RiskSummaryTitle>Flagged Risks</RiskSummaryTitle>
          <RiskSummaryValue>3</RiskSummaryValue>
        </RiskSummaryCard>
        
        <RiskSummaryCard>
          <RiskSummaryTitle>Risk Distribution</RiskSummaryTitle>
          <div style={{ width: '100%', height: 150 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={riskDistributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={0}
                  outerRadius={60}
                  paddingAngle={5}
                  dataKey="value"
                  label
                >
                  {riskDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </RiskSummaryCard>
      </RiskSummaryGrid>
      
      <h3>Flagged Risks</h3>
      <RiskList>
        {flaggedRisksData.map(risk => (
          <RiskItem key={risk.id}>
            <RiskHeader>
              <RiskIcon>!</RiskIcon>
              <RiskTitle>{risk.title}</RiskTitle>
            </RiskHeader>
            <RiskDescription>{risk.description}</RiskDescription>
          </RiskItem>
        ))}
      </RiskList>
      
      <Card title="Financial Stability">
        <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore</p>
      </Card>
      
      <h3>All Risks</h3>
      <FilterContainer>
        <FilterButton 
          active={severityFilter === 'All'} 
          onClick={() => setSeverityFilter('All')}
        >
          All
        </FilterButton>
        <FilterButton 
          active={severityFilter === 'High'} 
          onClick={() => setSeverityFilter('High')}
        >
          High
        </FilterButton>
        <FilterButton 
          active={severityFilter === 'Medium'} 
          onClick={() => setSeverityFilter('Medium')}
        >
          Medium
        </FilterButton>
        <FilterButton 
          active={severityFilter === 'Low'} 
          onClick={() => setSeverityFilter('Low')}
        >
          Low
        </FilterButton>
      </FilterContainer>
      
      <RiskTable>
        <thead>
          <tr>
            <th>Risk</th>
            <th>Category</th>
            <th>Severity</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {filteredRisks.map(risk => (
            <tr key={risk.id}>
              <td>{risk.title}</td>
              <td>{risk.category}</td>
              <td>
                <SeverityBadge severity={risk.severity as 'High' | 'Medium' | 'Low'}>
                  {risk.severity}
                </SeverityBadge>
              </td>
              <td>
                <StatusBadge status={risk.status as 'Open' | 'In Progress' | 'Closed'}>
                  {risk.status}
                </StatusBadge>
              </td>
            </tr>
          ))}
        </tbody>
      </RiskTable>
    </Layout>
  );
};

export default RisksPage;
